import axiosConnector from './axiosConnector';

export interface PricingConfig {
  id: number;
  equipment_type_id: number;
  equipmentType?: { id: number; name?: string; description: string };
  scenario_type: string;
  city_name: string | null;
  day_type: 'weekday' | 'weekend' | 'all';
  radius_km_min: number | null;
  radius_km_max: number | null;
  suggested_price: number;
  tech_amount: number;
  manu_amount: number;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SuggestedPriceResponse {
  hasConfig: boolean;
  suggestedPrice: number | null;
  techAmount: number | null;
  manuAmount: number | null;
  scenarioType: string | null;
  configId: number | null;
  showToRoles: string[];
  isIndependentTechnician?: boolean;
  debugContext?: {
    urgencyType: string | null;
    city: string | null;
    dayType: string | null;
    distanceKm: number | null;
    radiusTier: string | null;
  };
}

export interface FinancialTransaction {
  id: number;
  service_order_id: number | null;
  appointment_id: number;
  technician_id: number | null;
  admin_context_id: number | null;
  payer_user_id: number | null;
  equipment_type_id: number | null;
  scenario_type: string;
  pricing_config_id: number | null;
  gross_amount: number;
  tech_amount: number;
  manu_amount: number;
  tech_percentage_applied: number;
  manu_percentage_applied: number;
  is_personal_agenda: boolean;
  payment_status: 'pending' | 'paid' | 'cancelled' | 'disputed';
  payment_code: string | null;
  payment_id: string | null;
  is_forecast: boolean;
  payment_method: string | null;
  paid_at: string | null;
  city_at_transaction: string | null;
  day_type_at_transaction: string | null;
  created_at: string;
  appointment?: {
    id: number;
    scheduled_date: string;
    creator?: { id: number; name: string; email: string };
  };
  technician?: { id: number; name: string; email: string };
  adminContext?: { id: number; name: string; email: string };
}

export interface ConciliationEntry {
  technicianId: number;
  techName: string;
  paymentCode: string;
  paymentStatus: string;
  totalTech: number;
  count: number;
  paidAt: string | null;
  paymentId: string | null;
}

export interface TransactionListResponse {
  transactions: FinancialTransaction[];
  total: number;
  page: number;
  limit: number;
}

export interface TransactionSummary {
  totalGross: number;
  totalTech: number;
  totalManu: number;
  totalPaid: number;
  totalForecast: number;
  totalForecastManu: number;
  count: number;
}

export interface TransactionFilters {
  date_from?: string;
  date_to?: string;
  week?: number;
  year?: number;
  is_forecast?: boolean;
  technician_id?: number;
  admin_context_id?: number;
  payer_user_id?: number;
  equipment_type_id?: number;
  scenario_type?: string;
  payment_status?: string;
  page?: number;
  limit?: number;
}

export const getPricingConfigs = async (): Promise<PricingConfig[]> => {
  const response = await axiosConnector.get('/financials/pricing-config');
  return response.data as PricingConfig[];
};

export const createPricingConfig = async (data: Partial<PricingConfig>): Promise<PricingConfig> => {
  const response = await axiosConnector.post('/financials/pricing-config', data);
  return response.data as PricingConfig;
};

export const updatePricingConfig = async (id: number, data: Partial<PricingConfig>): Promise<PricingConfig> => {
  const response = await axiosConnector.patch(`/financials/pricing-config/${id}`, data);
  return response.data as PricingConfig;
};

export const deletePricingConfig = async (id: number): Promise<void> => {
  await axiosConnector.remove(`/financials/pricing-config/${id}`);
};

export interface UrgencyPreviewResponse {
  normal: { suggestedPrice: number; techAmount: number } | null;
  urgent: { suggestedPrice: number; techAmount: number } | null;
  emergency: { suggestedPrice: number; techAmount: number } | null;
}

export const getUrgencyPreview = async (equipmentTypeId: number): Promise<UrgencyPreviewResponse> => {
  const response = await axiosConnector.get(`/financials/pricing-config/urgency-preview?equipmentTypeId=${equipmentTypeId}`);
  return response.data as UrgencyPreviewResponse;
};

export const getPricingPreview = async (
  equipmentTypeId: number,
  scenario: string,
): Promise<{ suggestedPrice: number | null; techAmount: number | null; manuAmount: number | null }> => {
  const response = await axiosConnector.get(
    `/financials/pricing-config/preview?equipmentTypeId=${equipmentTypeId}&scenario=${scenario}`,
  );
  return response.data as { suggestedPrice: number | null; techAmount: number | null; manuAmount: number | null };
};

export const getSuggestedPrice = async (appointmentId: number, requestingTechnicianId?: number): Promise<SuggestedPriceResponse> => {
  const params = new URLSearchParams({ appointmentId: String(appointmentId) });
  if (requestingTechnicianId != null) params.set('requestingTechnicianId', String(requestingTechnicianId));
  const response = await axiosConnector.get(`/financials/pricing-config/suggest?${params.toString()}`);
  return response.data as SuggestedPriceResponse;
};

export const getTransactions = async (filters: TransactionFilters = {}): Promise<TransactionListResponse> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  const response = await axiosConnector.get(`/financials/transactions?${params.toString()}`);
  return response.data as TransactionListResponse;
};

export const getTransactionSummary = async (filters: TransactionFilters = {}): Promise<TransactionSummary> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  const response = await axiosConnector.get(`/financials/transactions/summary?${params.toString()}`);
  return response.data as TransactionSummary;
};

export const markTransactionAsPaid = async (id: number, paymentId?: string): Promise<FinancialTransaction> => {
  const response = await axiosConnector.patch(`/financials/transactions/${id}/mark-paid`, { payment_id: paymentId });
  return response.data as FinancialTransaction;
};

export const markTransactionAsDisputed = async (id: number): Promise<FinancialTransaction> => {
  const response = await axiosConnector.patch(`/financials/transactions/${id}/dispute`, {});
  return response.data as FinancialTransaction;
};

export const bulkMarkAsPaid = async (
  week: number,
  year: number,
  technicianId: number,
  paymentId?: string,
): Promise<{ updated: number }> => {
  const response = await axiosConnector.patch('/financials/transactions/bulk-mark-paid', {
    week,
    year,
    technician_id: technicianId,
    payment_id: paymentId,
  });
  return response.data as { updated: number };
};

export const getConciliation = async (week: number, year: number): Promise<ConciliationEntry[]> => {
  const response = await axiosConnector.get(`/financials/transactions/conciliation?week=${week}&year=${year}`);
  return response.data as ConciliationEntry[];
};
